import { useEffect, useRef, useState } from "react";
import { api, downloadBlobResponse, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import StickyHeader from "@/components/StickyHeader";
import {
    AlatRingkasanProdi, DaftarTabKeaktifan, KeteranganStatus, RingkasanProdiPerJenjang, TabelAkunKeaktifan, useTabKeaktifan,
} from "@/components/KeaktifanAkun";
import { TAB_KEAKTIFAN, bagianTampil, bulanIso, paramRingkasan } from "@/lib/keaktifan";
import { bolehResetSandiDariKeaktifan } from "@/lib/roles";
import { useAuth } from "@/context/AuthContext";
import ResetSandiUfpDialog from "@/components/ResetSandiUfpDialog";
import { toast } from "sonner";

// Indikator Keaktifan Akun (Super Admin & Admin UFP; rute dijaga ProtectedRoute + require_ufp_or_super).
// Hanya-baca: login terakhir dari Audit Log, simpan rotasi terakhir & terisi/wajib per prodi. Admin UFP tidak
// menerima email akun (server yang memutuskan lewat `dengan_email`). Dua tab memakai SATU respons yg sama;
// tab bawaan di sini = Ringkasan per Program Studi. Bulan ringkasan bisa dipilih (bawaan = bulan berjalan WIB dari
// server) & diunduh Excel/PDF; tab Status Akun tidak memakai angka bulanan.
export default function Keaktifan() {
    const [data, setData] = useState(null);
    const [tab, ubahTab] = useTabKeaktifan(TAB_KEAKTIFAN.prodi);
    const { user } = useAuth();
    // Reset kata sandi Admin Prodi/Koas: hanya Admin UFP (Super Admin memakai Akun Admin).
    const bolehReset = bolehResetSandiDariKeaktifan(user?.role);
    const [resetAkun, setResetAkun] = useState(null);
    const [bulan, setBulan] = useState(null); // "TTTT-BB"; null = bawaan server
    const [jenjang, setJenjang] = useState("semua"); // "semua" | "sp1" | "sp2" | "koas"
    const [memuat, setMemuat] = useState(false);
    const [mengunduh, setMengunduh] = useState("");
    const permintaanRef = useRef(0); // abaikan jawaban lama bila bulan diganti cepat
    useEffect(() => {
        const ke = ++permintaanRef.current;
        (async () => {
            setMemuat(true);
            try {
                // Tanpa ?jenjang=: server selalu mengirim SEMUA jenjang; filter Jenjang disaring di layar (bagianTampil).
                const params = paramRingkasan(bulan, "semua");
                const { data: d } = await api.get("/admin/keaktifan", Object.keys(params).length ? { params } : undefined);
                if (ke === permintaanRef.current) setData(d);
            } catch (e) {
                if (ke === permintaanRef.current) toast.error(formatApiErrorDetail(e.response?.data?.detail));
            } finally {
                if (ke === permintaanRef.current) setMemuat(false);
            }
        })();
    }, [bulan]);

    const unduh = async (format) => {
        const iso = bulanIso(data.bulan, data.tahun);
        setMengunduh(format);
        try {
            const res = await api.get(`/admin/keaktifan/prodi/export/${format}`, { params: paramRingkasan(iso, jenjang), responseType: "blob" });
            downloadBlobResponse(res, `aura_ringkasan-rotasi-prodi_${iso}_${jenjang === "semua" ? "semua-jenjang" : jenjang}.${format === "excel" ? "xlsx" : "pdf"}`);
        } catch {
            toast.error(`Gagal mengunduh ${format === "excel" ? "Excel" : "PDF"}`);
        } finally {
            setMengunduh("");
        }
    };

    return (
        <Tabs value={tab} onValueChange={ubahTab} className="max-w-6xl mx-auto px-6 lg:px-8 py-8 space-y-4" data-testid="halaman-keaktifan">
            <StickyHeader>
            <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Pemantauan</div>
                <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Plus Jakarta Sans" }}>
                    Keaktifan Akun
                </h1>
                <p className="text-sm text-slate-500 mt-1">
                    Akun mana yang rutin memakai AURA, dan prodi mana yang menyimpan rotasi. Tanpa data peserta.
                </p>
            </div>
            <DaftarTabKeaktifan />
            </StickyHeader>
            {!data ? (
                <div className="p-10 text-slate-500">Memuat data...</div>
            ) : (
                <>
                    <TabsContent value={TAB_KEAKTIFAN.akun}>
                        <Card className="border-slate-200 shadow-sm overflow-hidden">
                            <div className="px-4 py-2 border-b bg-slate-50 space-y-1">
                                <div className="text-xs font-semibold text-slate-600">Akun admin — terakhir login</div>
                                <KeteranganStatus ambang={data.ambang} />
                            </div>
                            <TabelAkunKeaktifan akun={data.akun} denganEmail={data.dengan_email}
                                onReset={bolehReset ? setResetAkun : undefined} />
                        </Card>
                    </TabsContent>
                    <TabsContent value={TAB_KEAKTIFAN.prodi}>
                        <Card className="border-slate-200 shadow-sm overflow-hidden">
                            <div className="px-4 py-2 border-b bg-slate-50 flex flex-wrap items-center justify-between gap-2">
                                <div className="text-xs font-semibold text-slate-600">
                                    Ringkasan per Program Studi (angka terisi/wajib sama dengan Monitoring Rotasi Klinik)
                                </div>
                                <AlatRingkasanProdi pilihan={data.pilihan_bulan || [bulanIso(data.bulan, data.tahun)]}
                                    bulan={bulanIso(data.bulan, data.tahun)} onBulan={setBulan}
                                    jenjang={jenjang} onJenjang={setJenjang} onUnduh={unduh}
                                    mengunduh={mengunduh} memuat={memuat} />
                            </div>
                            <RingkasanProdiPerJenjang bagian={bagianTampil(data.bagian, jenjang)} bulan={data.bulan} tahun={data.tahun} />
                        </Card>
                    </TabsContent>
                </>
            )}
            {bolehReset && <ResetSandiUfpDialog akun={resetAkun} onClose={() => setResetAkun(null)} />}
        </Tabs>
    );
}
